import { logger } from '@adornis/base/logging.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { getWhatsappTemplate } from '@adornis/buchungsheld-db/entities/mail-templates.js';
import { User } from '@adornis/buchungsheld-db/entities/user.js';
import { sendInstitutionEmail } from '@adornis/buchungsheld-db/mails/sendInstitutionMail.js';
import { defineMailTemplate } from '@adornis/mails/db/mail-template-definition.js';
import {
  ComponentParamaterType,
  ComponentSubType,
  ComponentType,
  MessageType,
} from '@adornis/whatsapp/server/types.js';
import { bhBaseQlContext } from '../bh-context.js';
import { ColorDefinitions } from '../entities/ColorDefinitions.js';
import { Institution } from '../entities/institution.js';
import { InstitutionMailTemplate, MailTemplateType, MailTemplateVars } from '../entities/mail-templates.js';
import { SettingColorDefintionProp } from '../entities/settings.js';
import { UserNotFoundError } from '../errors/UserNotFoundError.js';
import { getNotificationType, sendWhatsapp } from './_helper.js';

defineMailTemplate(MailTemplateType.OTP, {
  getMailData: arg1 => ({ data: {}, attachments: {}, scope: 'default' }),
  defaultTemplate: {
    subject: 'Login Code',
    content: `<p>Hallo {{title}} {{firstname}} {{lastname}},</p>
<p>Hiermit erhalten Sie ihren Login Code. Der Code ist nur kurze Zeit g&uuml;ltig. Geben Sie diesen nicht an dritte weiter:</p>
<p style="text-align: center;"><strong>{{otp-code}}</strong></p>
<p>Sollten Sie keine Anmeldung selbst vorgenommen haben, ignorieren Sie diese E-Mail und geben sie auf keinen Fall den Code an Dritte.</p>
<p>Viele Gr&uuml;&szlig;e</p>
<p>------------------------------------------------------------------</p>
<p>Hello {{title}} {{firstname}} {{lastname}},</p>
<p>you are receiving you're login token. This token is only valid for a short amount of time and should not be given to any third party:</p>
<p style="text-align: center;"><strong>{{otp-code}}</strong></p>
<p>If you did not register yourself, please ignore this e-mail and do not share this code with third parties.</p>
<p>Best regards</p>`,
    footer: '',
  },
  variables: InstitutionMailTemplate.getAvailableVariables(MailTemplateType.OTP),
  attachments: [],
  displayName: 'OTP',
  editorHint: () => InstitutionMailTemplate.getMailHint(MailTemplateType.OTP),
});

export async function notifyOtp(userID: string, code: string, institutionID?: string) {
  const user = await getByID(User, userID)(User.allFields);
  if (!user) throw new UserNotFoundError(userID);

  // Get institution with color definitions if institutionID is provided
  if (institutionID) {
    const institution = await getByID(
      Institution,
      institutionID,
    )({
      _id: 1,
      name: 1,
      colorDefintions: selectionSet(() => ColorDefinitions, 4),
      settings: {
        colorDefinitions: selectionSet(() => ColorDefinitions, 4),
        inheritedColorDefinitions: selectionSet(() => SettingColorDefintionProp, 4),
      },
    });

    // Set mail theme
    if (institution) {
      bhBaseQlContext.colorDefinitions =
        institution?.settings?.colorDefinitions ?? institution?.settings?.inheritedColorDefinitions?.value;
    }
  }

  const mailTemplate = await InstitutionMailTemplate.getMailTemplateForInstitution(
    institutionID,
    MailTemplateType.OTP,
  )(InstitutionMailTemplate.allFields);

  const notificationType = getNotificationType(user, mailTemplate);
  if (notificationType === 'whatsapp') {
    await sendWhatsapp({
      to: user.whatsappPhone!,
      type: MessageType.TEMPLATE,
      content: {
        template: {
          name: getWhatsappTemplate(MailTemplateType.OTP).name,
          language: {
            code: 'de',
          },
          components: [
            {
              type: ComponentType.BODY,
              parameters: [
                {
                  type: ComponentParamaterType.TEXT,
                  text: code,
                },
              ],
            },
            {
              type: ComponentType.BUTTON,
              sub_type: ComponentSubType.URL,
              index: '0',
              parameters: [
                {
                  type: ComponentParamaterType.TEXT,
                  text: code,
                },
              ],
            },
          ],
        },
      },
    });
  } else if (notificationType === 'email') {
    const mailContent = await mailTemplate.buildEmail({
      [MailTemplateVars.TITLE]: user.title ?? '',
      [MailTemplateVars.FIRST_NAME]: InstitutionMailTemplate.mapFirstNameVar(user),
      [MailTemplateVars.LAST_NAME]: InstitutionMailTemplate.mapLastNameVar(user),
      [MailTemplateVars.OTP]: InstitutionMailTemplate.mapOTPVar(code),
    } as any);

    if (!mailContent) throw new Error("mail template couldn't be found.");

    await sendInstitutionEmail({
      email: user.email,
      subject: mailTemplate.subject,
      content: mailContent,
      institutionID,
    });
  } else {
    // no notification send because both are deactivated
    logger.warn(
      { notificationType, notification: mailTemplate.emailType },
      'no notification send because neither email nor whatsapp are activated',
    );
  }
}
